import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, TooltipHost, useTheme} from '@fluentui/react';
import {RootState} from '../../store/rootReducer';
import {updateSearchCriteria, updateSelectedSaveSearchId} from '../../store/searchSlice';

interface IManufacturerFilterLinkProps {
    manufacturer?: string;
}

/**
 * The Manufacturer cell on the search grid. With no manufacturer filter in play, the name is a link
 * that narrows the search to that manufacturer (the results follow the redux criteria, so dispatching
 * is the whole search). Once a manufacturer filter is set the cell is plain text: the criteria panel
 * owns changing or clearing it.
 */
const ManufacturerFilterLink: React.FC<IManufacturerFilterLinkProps> = ({manufacturer}) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const criteria = useSelector((state: RootState) => state.search.criteria);
    if (!manufacturer) return null;
    const alreadyFiltered = (criteria.manufacturer?.length ?? 0) > 0;
    if (alreadyFiltered) return <span>{manufacturer}</span>;

    return (
        <TooltipHost content={`Show only ${manufacturer} products`}>
            <Link
                onClick={(e) => {
                    e.stopPropagation();
                    dispatch(updateSearchCriteria({...criteria, manufacturer: [manufacturer], page: 1}));
                    dispatch(updateSelectedSaveSearchId(undefined));
                }}
                // Reads as plain text at rest; the brand green only appears on hover so the grid
                // is not a column of links.
                styles={{
                    root: {
                        fontWeight: 600,
                        color: theme.palette.neutralPrimary,
                        selectors: {
                            ':hover': {color: theme.palette.themePrimary, textDecoration: 'underline'},
                            ':active': {color: theme.palette.themeDark},
                        },
                    },
                }}
            >
                {manufacturer}
            </Link>
        </TooltipHost>
    );
};

export default ManufacturerFilterLink;
